import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AccumulatedNode, AccumulatedGraph } from '../../types';

import { NODE_COLORS } from '../../lib/entityColors';

const PROP_LABELS: Record<string, string> = {
  ioc_type: 'IOC Type',
  infrastructure: 'Infrastructure',
  cvss_score: 'CVSS Score',
  cvss_severity: 'Severity',
  published_date: 'Published',
  in_cisa_kev: 'CISA KEV',
  cwe_ids: 'CWE',
  country: 'Country',
  platform: 'Platform',
  attack_id: 'ATT&CK ID',
  title: 'Title',
  date: 'Date',
  organization: 'Organization',
  alt_names_count: 'Alt Names',
  description: 'Description',
};

interface NodeDetailsPanelProps {
  node: AccumulatedNode;
  graph: AccumulatedGraph;
  onClose?: () => void;
  onFocusNode: (nodeId: string) => void;
  messages: { id: string; role: string; content: string }[];
  /** Optional: show documents popover callback (used in explorer mode) */
  onShowDocs?: (anchorEl: HTMLElement) => void;
  /** Optional: document count for the node */
  docCount?: number;
}

export const NodeDetailsPanel: React.FC<NodeDetailsPanelProps> = ({
  node,
  graph,
  onClose,
  onFocusNode,
  messages,
  onShowDocs,
  docCount,
}) => {
  const navigate = useNavigate();

  const handleGoToDocument = () => {
    navigate(`/sources/${encodeURIComponent(node.name)}`);
  };

  const color = NODE_COLORS[node.label] ?? '#aaa';

  // Find connected nodes via edges
  const connectedNodes: { nodeId: string; name: string; label: string; relType: string }[] = [];
  for (const edge of graph.edges) {
    if (edge.source === node.id) {
      const target = graph.nodes.find((n) => n.id === edge.target);
      if (target) {
        connectedNodes.push({
          nodeId: target.id,
          name: target.name,
          label: target.label,
          relType: edge.rel_type,
        });
      }
    } else if (edge.target === node.id) {
      const source = graph.nodes.find((n) => n.id === edge.source);
      if (source) {
        connectedNodes.push({
          nodeId: source.id,
          name: source.name,
          label: source.label,
          relType: edge.rel_type,
        });
      }
    }
  }

  // Resolve source query IDs to user message snippets
  const resolveQueryLabel = (queryId: string): string => {
    const msg = messages.find((m) => m.id === queryId && m.role === 'user');
    if (msg) {
      const snippet = msg.content.length > 40 ? msg.content.slice(0, 40) + '...' : msg.content;
      return snippet;
    }
    return queryId;
  };

  return (
    <div className="malpedia-node-details-panel">
      {/* Header */}
      <div className="malpedia-node-details-header">
        <div className="malpedia-node-details-header-info">
          <span className="malpedia-node-details-name">{node.name}</span>
          <span
            className="malpedia-node-details-type-badge"
            style={{ backgroundColor: color }}
          >
            {node.label}
          </span>
        </div>
        {onClose && (
          <button
            className="malpedia-node-details-close"
            onClick={onClose}
            title="Close details"
          >
            x
          </button>
        )}
      </div>

      {/* Document cite_key */}
      {node.label === 'Document' && (
        <div className="malpedia-node-details-cite-key">
          <span>{node.name}</span>
          {node.name.startsWith('web:') ? (
            <a
              className="malpedia-node-details-go-to-doc"
              href={node.name.replace(/^web:/, 'https://')}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: 'none' }}
            >
              Open URL
            </a>
          ) : (
            <button
              className="malpedia-node-details-go-to-doc"
              onClick={handleGoToDocument}
            >
              Go to Document
            </button>
          )}
        </div>
      )}

      {/* Actions: doc badge */}
      {docCount != null && docCount > 0 && (
        <div className="malpedia-node-details-actions">
          <button
            className="malpedia-node-details-action-btn malpedia-node-details-doc-badge"
            onClick={(e) => onShowDocs?.(e.currentTarget)}
          >
            {docCount} doc{docCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Properties section */}
      <div className="malpedia-node-details-section">
        <div className="malpedia-node-details-section-title">Properties</div>
        <div className="malpedia-node-details-prop">
          <span className="malpedia-node-details-prop-label">Type</span>
          <span
            className="malpedia-node-details-type-badge malpedia-node-details-type-badge--small"
            style={{ backgroundColor: color }}
          >
            {node.label}
          </span>
        </div>
        {node.score != null && (
          <div className="malpedia-node-details-prop">
            <span className="malpedia-node-details-prop-label">Score</span>
            <span className="malpedia-node-details-prop-value">
              {node.score.toFixed(3)}
            </span>
          </div>
        )}
        {node.strategy && (
          <div className="malpedia-node-details-prop">
            <span className="malpedia-node-details-prop-label">Strategy</span>
            <span className="malpedia-node-details-prop-value">{node.strategy}</span>
          </div>
        )}
        <div className="malpedia-node-details-prop">
          <span className="malpedia-node-details-prop-label">Pinned</span>
          <span className="malpedia-node-details-prop-value">
            {node.pinned ? 'yes' : 'no'}
          </span>
        </div>
        {node.properties && Object.entries(node.properties).map(([key, val]) => {
          if (val == null || val === '' || val === false) return null;
          const label = PROP_LABELS[key] || key.replace(/_/g, ' ');
          let display = String(val);
          if (key === 'in_cisa_kev' && val === true) display = 'Yes';
          if (key === 'description' && display.length > 120) display = display.slice(0, 120) + '...';
          return (
            <div key={key} className="malpedia-node-details-prop">
              <span className="malpedia-node-details-prop-label">{label}</span>
              <span className="malpedia-node-details-prop-value">{display}</span>
            </div>
          );
        })}
      </div>

      {/* Provenance section */}
      <div className="malpedia-node-details-section">
        <div className="malpedia-node-details-section-title">Provenance</div>
        <div className="malpedia-node-details-prop">
          <span className="malpedia-node-details-prop-label">Added at</span>
          <span className="malpedia-node-details-prop-value">
            query #{node.addedAtQueryIndex + 1}
          </span>
        </div>
        <div className="malpedia-node-details-prop">
          <span className="malpedia-node-details-prop-label">Last seen</span>
          <span className="malpedia-node-details-prop-value">
            query #{node.lastSeenAtQueryIndex + 1}
          </span>
        </div>
        {node.sourceQueryIds.length > 0 && (
          <div className="malpedia-node-details-queries">
            <span className="malpedia-node-details-prop-label">Source queries</span>
            <ul className="malpedia-node-details-query-list">
              {node.sourceQueryIds.map((qid) => (
                <li key={qid} className="malpedia-node-details-query-item">
                  {resolveQueryLabel(qid)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* Connected nodes section */}
      {connectedNodes.length > 0 && (
        <div className="malpedia-node-details-section">
          <div className="malpedia-node-details-section-title">
            Connected nodes ({connectedNodes.length})
          </div>
          <ul className="malpedia-node-details-connected-list">
            {connectedNodes.map((cn, i) => (
              <li
                key={`${cn.nodeId}-${i}`}
                className="malpedia-node-details-connected-item"
                onClick={() => onFocusNode(cn.nodeId)}
                title={`${cn.relType} - click to focus`}
              >
                <span
                  className="malpedia-node-details-connected-dot"
                  style={{ backgroundColor: NODE_COLORS[cn.label] ?? '#aaa' }}
                />
                <span className="malpedia-node-details-connected-name">{cn.name}</span>
                <span className="malpedia-node-details-connected-type">{cn.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
